import type { Entry } from "contentful";
import { Link, type LinkProps } from "react-router";

import { useContentfulInspectorMode } from "@contentful/live-preview/react";

import { cn } from "~/lib/ui";

import type { TypeImageSkeleton } from "../compiled";
import type { LinkContent } from "../types";
import { ContentfulImage } from "./contentful-image";

const ICON_POSITION = {
  "Append Text": "flex-row",
  "Prepend Text": "flex-row-reverse",
} as const;

type IconPosition = keyof typeof ICON_POSITION;

// Extended link type that includes additional fields that may be present
type ExtendedLinkFields = {
  title?: string;
  link: string;
  linkText?: string;
  iconPosition?: IconPosition;
  icon?: Entry<TypeImageSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>;
};

type ExtendedLinkContent = {
  sys: LinkContent["sys"];
  fields: ExtendedLinkFields;
};

export const ContentfulLink = ({
  content,
  className,
  ...linkProps
}: {
  content: LinkContent;
  className?: string;
} & Omit<LinkProps, "content" | "to">) => {
  const extendedContent = content as ExtendedLinkContent;
  const fields = extendedContent.fields;
  const inspectorProps = useContentfulInspectorMode();

  const link = fields.link ?? "";
  const iconPosition = fields.iconPosition;

  return (
    <Link
      className={cn(
        className,
        ICON_POSITION[iconPosition || "Append Text"],
        "flex w-fit items-center gap-2",
      )}
      to={link}
      title={fields.title}
      {...inspectorProps({ entryId: content.sys.id, fieldId: "name" })}
      {...linkProps}
    >
      {fields.linkText || fields.title || "Link"}
      {fields.icon?.fields.image && (
        <ContentfulImage
          imageUrl={fields.icon.fields.image.fields.file?.url || ""}
          alt={fields.icon.fields.alt || "Link Icon"}
          className="h-5 w-5"
          icon
        />
      )}
    </Link>
  );
};
